import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiClient } from '../services/base';
import AuthLayout from '../components/AuthLayout';
import AuthLogo from '../components/AuthLogo';
import { plumFieldSx, darkButtonSx, cancelLinkSx, PLUM } from '../components/authStyles';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim()) {
      setError('Please enter your username or email address.');
      setLoading(false);
      return;
    }

    try {
      await apiClient.post('/auth/forgot-password', { email: email.trim() });
      setSuccess(true);
    } catch (err: any) {
      // Show success even on error to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mb: { xs: 8, md: 10 } }}>
          <AuthLogo />
        </Box>

        <Typography
          component="h1"
          sx={{ fontSize: '24px', fontWeight: 700, color: PLUM, textAlign: 'center', mb: 3 }}
        >
          Forgot your Password?
        </Typography>

        <Typography sx={{ fontSize: '20px', color: PLUM, textAlign: 'center', mb: 3 }}>
          Don't worry it happens all the time!
        </Typography>

        <Typography
          sx={{
            fontSize: '16px',
            lineHeight: 1.5,
            letterSpacing: '-0.01em',
            color: '#1a1a1a',
            textAlign: 'center',
            // Even out line lengths so centered copy never leaves a one-word last line.
            textWrap: 'balance',
            mb: { xs: 10, md: 12 },
          }}
        >
          Enter your email address below and we will resend your login details to the registered
          email address.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
            If an account exists with that email, login details have been sent.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <TextField
              fullWidth
              placeholder="Enter Username / Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              disabled={loading}
              autoFocus
              autoComplete="username"
              sx={{ ...plumFieldSx, mb: 2.5 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              disableElevation
              sx={{ ...darkButtonSx, mb: 4 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Recover Login Details'}
            </Button>
          </Box>
        )}

        <Typography component={RouterLink} to="/login" sx={cancelLinkSx}>
          Cancel
        </Typography>
      </Box>
    </AuthLayout>
  );
};

export default ForgotPassword;
